import { useEffect, useState } from "react";
import { Palette, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const PREFS_KEY = "inksight-kanban-preferences-v1";

const PHASES = [
  { id: "ready_for_submission", label: "Graphics Prepress", color: "#78350f" },
  { id: "artwork_submitted", label: "Artwork Submitted", color: "#1e3a8a" },
  { id: "pdf_proof", label: "PDF Proof", color: "#164e63" },
  { id: "swatch", label: "Swatch", color: "#7c2d12" },
  { id: "pilot", label: "Pilot", color: "#581c87" },
  { id: "graphics_complete", label: "Graphics Complete", color: "#064e3b" },
];

const DEFAULT_COLORS = Object.fromEntries(
  PHASES.map((phase) => [phase.id, phase.color]),
);

const loadPrefs = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
    return {
      density: saved.density === "comfortable" ? "comfortable" : "compact",
      colors: { ...DEFAULT_COLORS, ...(saved.colors || {}) },
    };
  } catch {
    return {
      density: "compact",
      colors: { ...DEFAULT_COLORS },
    };
  }
};

export const KanbanAppearanceSettings = () => {
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const reset = () => {
    const defaults = {
      density: "compact",
      colors: { ...DEFAULT_COLORS },
    };
    setPrefs(defaults);
    toast.success("Kanban appearance reset to defaults");
  };

  return (
    <section className="mt-8 max-w-4xl bg-card border border-border rounded-xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Palette size={20} />
            Kanban Appearance
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Control Kanban card density and workflow category colors.
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-2 px-3 py-2 bg-secondary rounded-lg text-sm"
        >
          <RotateCcw size={15} />
          Reset Defaults
        </button>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Card Density</label>
          <select
            value={prefs.density}
            onChange={(event) => setPrefs((previous) => ({
              ...previous,
              density: event.target.value,
            }))}
            className="w-full max-w-xs px-4 py-2.5 bg-background border border-border rounded-lg"
          >
            <option value="compact">Compact</option>
            <option value="comfortable">Comfortable</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Compact is optimized for viewing more projects at once.
          </p>
        </div>

        <div>
          <div className="mb-3">
            <h3 className="text-sm font-medium">Category Colors</h3>
            <p className="text-xs text-muted-foreground mt-1">
              These colors control the workflow category columns. Individual project colors can still be changed directly on the Kanban card.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {PHASES.map((phase) => (
              <label
                key={phase.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3"
              >
                <input
                  type="color"
                  value={prefs.colors[phase.id] || phase.color}
                  onChange={(event) => setPrefs((previous) => ({
                    ...previous,
                    colors: {
                      ...previous.colors,
                      [phase.id]: event.target.value,
                    },
                  }))}
                  className="h-9 w-12 border-0 bg-transparent p-0 cursor-pointer"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{phase.label}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {prefs.colors[phase.id] || phase.color}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
